import { API_SPECIFICATION_MODEL, APPLICATION_GRAPH, CONTROLLED_VOCABULARY_MODEL, LOCAL_PACKAGE, LOCAL_SEMANTIC_MODEL, VISUAL_MODEL, QUERYABLE_MODEL, RDFS_MODEL, V1 } from "@dataspecer/core-v2/model/known-models";
import { LanguageString } from "@dataspecer/core/core/core-resource";
import { AppWindowMac, BookOpen, Code, Cog, Eye, Folder, Globe2, LibraryBig } from "lucide-react";
import { v4 as uuidv4 } from 'uuid';
import { cn } from "./lib/utils";
import { packageService, requestLoadPackage } from "./package";

export function getCMELink(packageId: string, viewId: string) {
  return import.meta.env.VITE_CME + "/diagram?package-id=" + encodeURIComponent(packageId) + "&view-id=" + encodeURIComponent(viewId)
}

export function getSchemaLink(packageId: string) {
  return (import.meta.env.VITE_DATA_SPECIFICATION_EDITOR ?? "") + "/specification?dataSpecificationIri=" + encodeURIComponent(packageId);
}

export function getHistoryLink(packageId: string) {
  return (import.meta.env.VITE_HISTORY ?? "") + "/evolution?packageIri=" + encodeURIComponent(packageId);
}

export function getEvolutionReviewLink(packageId: string, branch: number) {
  return (import.meta.env.VITE_HISTORY ?? "") + "/evolution/review?packageIri=" + encodeURIComponent(packageId) + "&branch=" + encodeURIComponent(branch);
}

export interface createModelContext {
  iri?: string;
  parentIri: string;
  modelType?: string;
  label?: LanguageString;
  description?: LanguageString;
  baseIri?: string;
  caches?: string[];
}

function getHookForStandardModel(type: string, initialContent: (iri: string, context: createModelContext) => any) {
  return async (context: createModelContext) => {
    const iri = context.iri ? context.iri : uuidv4();
    await fetch(import.meta.env.VITE_BACKEND + "/resources?parentIri=" + encodeURIComponent(context.parentIri), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        iri: iri,
        type: type,
        userMetadata: {
          label: context.label,
          description: context.description,
        }
      }),
    });
    await fetch(import.meta.env.VITE_BACKEND + "/resources/blob?iri=" + encodeURIComponent(iri), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(initialContent(iri, context)),
    });
    await requestLoadPackage(context.parentIri, true);
    return iri;
  }
}

export const createModelInstructions = {
  [LOCAL_PACKAGE as string]: {
    needsNaming: true,
    createHook: async (context: createModelContext) => {
      const iri = uuidv4();
      await packageService.createPackage(context.parentIri, {
        iri,
        userMetadata: {
          label: context.label,
          description: context.description,
        }
      });
      await fetch(import.meta.env.VITE_BACKEND + "/resources/blob?iri=" + encodeURIComponent(iri), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      await requestLoadPackage(context.parentIri, true);
      return iri;
    }
  },
  [API_SPECIFICATION_MODEL]: {
    needsNaming: true,
    createHook: getHookForStandardModel(API_SPECIFICATION_MODEL, () => {}),
  },
  [V1.PSM]: {
    needsNaming: false,
    createHook: async (context: createModelContext) => {
      await getHookForStandardModel(V1.PSM, iri => ({operations: [], resources: {
        [iri]: {
          "types": [
              "https://ofn.gov.cz/slovník/psm/Schema"
          ],
          "iri": iri,
          "dataPsmHumanLabel": null,
          "dataPsmHumanDescription": null,
          "dataPsmTechnicalLabel": null,
          "dataPsmRoots": [],
          "dataPsmParts": []
        }
      }}))(context);
    },
  },
  [VISUAL_MODEL]: {
    needsNaming: false,
    createHook: getHookForStandardModel(VISUAL_MODEL, iri => ({
      "identifier": iri,
      "version": 1,
      "type": VISUAL_MODEL,
      "entities": [
        {
          "identifier": iri,
          "type": [
            "entity-model-type"
          ],
          "label": {
            "en": "Main view"
          }
        }
      ]
    })),
  },
  [APPLICATION_GRAPH]: {
    needsNaming: true,
    createHook: getHookForStandardModel(APPLICATION_GRAPH, (id, context) => ({
      "id": id,
      "label": context.label?.cs ?? context.label?.en ?? "Application",
      "datasources": [],
      "nodes": [],
      "edges": [],
      "dataSpecification": []
    })),
  },
  [LOCAL_SEMANTIC_MODEL]: {
    needsNaming: false,
    createHook: getHookForStandardModel(LOCAL_SEMANTIC_MODEL, (iri, context) => ({
      "type": LOCAL_SEMANTIC_MODEL,
      "modelId": iri,
      "label": context.label ?? {},
      "baseIri": context.baseIri ?? iri,
      "entities": {}
    })),
  },
  [V1.GENERATOR_CONFIGURATION]: {
      needsNaming: false,
      createHook: getHookForStandardModel(V1.GENERATOR_CONFIGURATION, () => ({})),
    },
  }

export const modelTypeToName = {
    [LOCAL_PACKAGE]: "Directory",
    [VISUAL_MODEL]: "Visual model",
    [LOCAL_SEMANTIC_MODEL]: "Semantic model",
    [V1.PSM]: "PSM",
    [V1.GENERATOR_CONFIGURATION]: "Generator configuration",
    [QUERYABLE_MODEL]: "SSP",
    [RDFS_MODEL]: "PIM Wrapper",
    [API_SPECIFICATION_MODEL]: "OpenAPI Specification",
    [APPLICATION_GRAPH]: "Application graph",
    [CONTROLLED_VOCABULARY_MODEL]: "Controlled vocabulary",
  };

export const ModelIcon = ({ type, className }: { type: string[], className?: string }) => {
  if (type.includes(APPLICATION_GRAPH)) {
    return <AppWindowMac className={cn("text-rose-600", className)} />
  }
  if (type.includes(LOCAL_PACKAGE)) {
    return <Folder className={cn("text-gray-400", className)} />;
  }
  if (type.includes(VISUAL_MODEL)) {
    return <Eye className={cn("text-purple-400", className)} />;
  }
  if (type.includes(LOCAL_SEMANTIC_MODEL)) {
    return <LibraryBig className={cn("text-yellow-400", className)} />;
  }
  if (type.includes(V1.PSM)) {
    return <Code className={cn("text-red-400", className)} />;
  }
  if (type.includes(V1.GENERATOR_CONFIGURATION)) {
    return <Cog className={cn("text-purple-400", className)} />;
  }
  if (type.includes(QUERYABLE_MODEL)) {
    return <Globe2 className={cn("text-green-400", className)} />;
  }
  if (type.includes(RDFS_MODEL)) {
    return <LibraryBig className={cn("text-orange-400", className)} />;
  }
  if (type.includes(API_SPECIFICATION_MODEL)) {
    return <span className={cn("aspect-square w-[24px] leading-[24px] align-middle text-center text-blue-800 font-bold text-xs", className)} >API</span>;
  }
  if (type.includes(CONTROLLED_VOCABULARY_MODEL)) {
    return <BookOpen className={cn("text-teal-400", className)} />;
  }
};
